import { useEffect, useState } from 'react'
import { Button, Col, DatePicker, Divider, Input, Pagination, Row, Space, Table } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import 'moment/locale/vi'
import locale from 'antd/es/date-picker/locale/vi_VN'
import { isEmpty, trim } from 'lodash'
import { DATE_FORMAT_CLIENT, DATE_TIME_FORMAT_CLIENT_short2, PAGE_SIZE, getPage } from 'src/helpers/constants'
import { formatCurrency, showMessageError, showMessageSuccess } from 'src/helpers/functions'
import useModal from 'src/helpers/modal/useModal'
import { useStore } from 'src/hooks'
import { Loading } from 'src/common'
import ImagePreview, { getImageUrl, isImageUrl } from 'src/common/components/ImagePreview'
import CreateOrder from '../order/Create'
import SelectProductModal from '../order/SelectProductModal'
import useTableScrollY from 'src/common/useTableScrollY'
import ExportToCSV from './ExportToCSV'

const DraftOrderManagement = observer(() => {
   const OrdersStore = useStore('OrdersStore')
   const AuthStore = useStore('AuthStore')
   const { data_getUserInfo } = AuthStore
   const { rows, count } = OrdersStore.orders
   const { confirm } = useModal()
   const { paginationRef, scrollY, tableWrapperRef } = useTableScrollY({
      dependencyKey: `${rows?.length || 0}-${count}`
   })

   const [textSearch, setTextSearch] = useState('')
   const [visibleCreate, setVisibleCreate] = useState(false)
   const [visibleDetail, setVisibleDetail] = useState(false)
   const [visibleProductModal, setVisibleProductModal] = useState(false)
   const [selectedRows, setSelectedRows] = useState<any[]>([])
   const [selectedItem, setSelectedItem] = useState<any>(null)
   const [isLoading, setIsLoading] = useState(false)

   useEffect(() => {
      getListData(0, { keyword: textSearch })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [textSearch, OrdersStore.startTime, OrdersStore.endTime])

   const getListData = async (currentPage: number, param?: any) => {
      try {
         setIsLoading(true)
         const offset = getPage(currentPage, PAGE_SIZE)
         await OrdersStore.getDraftOrderList({ offset, ...param })
      } catch (error) {
         console.error(error)
      } finally {
         setIsLoading(false)
      }
   }

   const refreshCurrentPage = () => {
      getListData(OrdersStore.offsetList / PAGE_SIZE, { keyword: trim(textSearch) })
   }

   const handleRangePickerChange = (date: any) => {
      OrdersStore.setStartTime(date[0])
      OrdersStore.setEndTime(date[1])
   }

   const handleCreateDone = () => {
      setVisibleCreate(false)
      refreshCurrentPage()
   }

   const getProductsInOrder = (record: any) => {
      return record?.listProduct || record?.products_orders || record?.listProductInOrder || []
   }

   const formatDataProducts = (list: any, order?: any) => {
      return (list || []).map((item: any) => {
         const product = item?.product || item
         return {
            name: product?.name,
            id: product?.id,
            images: product?.images,
            base_cost: item?.baseCostSellter || product?.base_cost,
            qty: item?.qty,
            tags: product?.tags,
            linkMockup: item?.linkMockup || item?.mockup || product?.linkMockup || order?.linkMockup || '',
            linkDesign: item?.linkDesign || item?.design || product?.linkDesign || order?.linkDesign || ''
         }
      })
   }

   const handleShowDetail = async (item: any) => {
      try {
         setIsLoading(true)
         const result = await OrdersStore.getDraftOrderDetail(item.draftId)
         const order = result?.order || result?.draftOrder || result?.data?.order || result?.data || item
         const listProduct =
            result?.listProductInOrder ||
            result?.listProduct ||
            result?.data?.listProductInOrder ||
            result?.data?.listProduct ||
            getProductsInOrder(order)

         setSelectedItem({ ...item, ...order, draftId: order?.draftId || item?.draftId })
         OrdersStore.setProductsSelected(formatDataProducts(listProduct, order))
         setVisibleDetail(true)
      } catch (error) {
         console.error(error)
      } finally {
         setIsLoading(false)
      }
   }

   const hideDetail = () => {
      setVisibleDetail(false)
      setSelectedItem(null)
      OrdersStore.setProductsSelected([])
   }

   const handleRemoveOrder = (id: string) => {
      confirm({
         title: 'Bạn xác nhận xoá đơn nháp?',
         icon: <ExclamationCircleOutlined />,
         okText: 'Xác nhận',
         cancelText: 'Huỷ',
         async onCancel() {},
         async onOk() {
            await OrdersStore.removeDraftOrder(id)
            setSelectedRows((prev) => prev.filter((order: any) => order.draftId !== id))
            refreshCurrentPage()
         }
      })
   }

   const getDraftOrderSubTotal = (order: any) => {
      return parseFloat(order?.subTotalPrice || 0)
   }

   const getCreateOrderLabel = (order: any) => {
      return `$${formatCurrency(getDraftOrderSubTotal(order))} • Thanh toán`
   }

   const getCreateMultiOrderLabel = () => {
      const total = selectedRows.reduce((sum: number, order: any) => sum + getDraftOrderSubTotal(order), 0)
      return `$${formatCurrency(total)} • Thanh toán`
   }

   const handleConfirmOrder = (order: any, onSuccess?: () => void) => {
      const draftOrderId = order?.draftId || order?.id
      const total = getDraftOrderSubTotal(order)
      const balance = parseFloat(data_getUserInfo?.wallet?.balance || 0)

      confirm({
         title: 'Thanh toán đơn nháp?',
         icon: <ExclamationCircleOutlined />,
         content: `Tổng tiền cần thanh toán: $${formatCurrency(total)}. Số dư hiện tại: $${formatCurrency(balance)}.`,
         okText: 'Thanh toán',
         cancelText: 'Huỷ',
         async onOk() {
            if (balance < total) {
               showMessageError('Số dư không đủ để thanh toán đơn nháp.')
               return
            }

            await OrdersStore.confirmDraftOrder(draftOrderId)
            showMessageSuccess('Thanh toán đơn nháp thành công.')
            setSelectedRows((prev) =>
               prev.filter((selectedOrder: any) => (selectedOrder.draftId || selectedOrder.id) !== draftOrderId)
            )
            onSuccess?.()
            refreshCurrentPage()
         }
      })
   }

   const handleConfirmSelected = () => {
      const ids = selectedRows.map((order: any) => order.draftId)
      const total = selectedRows.reduce((sum: number, order: any) => sum + getDraftOrderSubTotal(order), 0)
      confirm({
         title: 'Thanh toán các đơn nháp đã chọn?',
         icon: <ExclamationCircleOutlined />,
         content: `Xác nhận thanh toán ${selectedRows.length} đơn - tổng cộng $${formatCurrency(total)}.`,
         okText: 'Thanh toán',
         cancelText: 'Huỷ',
         async onOk() {
            await OrdersStore.confirmMultiDraftOrders(ids)
            showMessageSuccess('Thanh toán các đơn nháp thành công.')
            setSelectedRows([])
            refreshCurrentPage()
         }
      })
   }

   const getProductLink = (item: any, record: any, field: 'linkMockup' | 'linkDesign') => {
      const link =
         item?.[field] ||
         item?.product?.[field] ||
         (field === 'linkMockup' ? item?.mockup || item?.product?.mockup : item?.design || item?.product?.design) ||
         record?.[field] ||
         ''

      return typeof link === 'string' ? link.trim() : ''
   }

   const renderProductRows = (record: any, type: 'product' | 'mockup' | 'design') => {
      const products = getProductsInOrder(record)

      if (products.length === 0) return <span>--</span>

      return (
         <div className="draft-product-list">
            {products.map((item: any, index: number) => {
               const product = item?.product || item
               const field = type === 'mockup' ? 'linkMockup' : 'linkDesign'
               const link = type === 'product' ? '' : getProductLink(item, record, field)

               return (
                  <div className="draft-product-row" key={`${type}-${product?.id || product?.name || index}`}>
                     {type === 'product' && (
                        <>
                           <ImagePreview
                              src={getImageUrl(product?.images?.[0] || '', process.env.REACT_APP_IMAGE_ENDPOINT)}
                              size={64}
                              fit="contain"
                           />
                           <span className="txtMax2Line draft-product-name">{product?.name}</span>
                        </>
                     )}

                     {type !== 'product' &&
                        (link ? (
                           isImageUrl(link) ? (
                              <ImagePreview src={link} size={64} fit="contain" />
                           ) : (
                              <a href={link} target="_blank" rel="noreferrer" className="gx-link draft-link-text">
                                 {link}
                              </a>
                           )
                        ) : (
                           <span className="draft-empty-link">--</span>
                        ))}
                  </div>
               )
            })}
         </div>
      )
   }

   const columns = [
      {
         title: 'OrderID',
         dataIndex: 'code',
         key: 'code',
         width: 80,
         render: (value: any) => (
            <div className="draft-order-code">
               <span className="txtMax2Line">{value}</span>
               <span className="draft-order-badge">Nháp</span>
            </div>
         )
      },
      {
         title: 'Created',
         dataIndex: 'createdAt',
         key: 'createdAt',
         width: 80,
         render: (value: any) => (
            <span className="txtMax2Line">{moment(value).format(DATE_TIME_FORMAT_CLIENT_short2)}</span>
         )
      },
      {
         title: 'Products',
         dataIndex: 'listProduct',
         key: 'listProduct',
         width: 130,
         render: (_: any, record: any) => renderProductRows(record, 'product')
      },
      {
         title: 'Link mockup',
         dataIndex: 'listProduct',
         width: 120,
         key: 'linkMockup',
         render: (_: any, record: any) => renderProductRows(record, 'mockup')
      },
      {
         title: 'Link design',
         dataIndex: 'listProduct',
         width: 120,
         key: 'linkDesign',
         render: (_: any, record: any) => renderProductRows(record, 'design')
      },
      {
         title: 'Dịch vụ',
         dataIndex: 'serviceOrders',
         width: 120,
         key: 'serviceOrders',
         render: (value: any) => {
            if (!value || value.length === 0) return <span>--</span>
            return (
               <div style={{ fontSize: 11 }}>
                  {value.map((s: any, index: number) => (
                     <div key={index} className="txtMax2Line">
                        {s.qty > 1 ? `(${s.qty})` : ''} {s.serviceName}
                     </div>
                  ))}
               </div>
            )
         }
      },
      {
         title: 'Updated',
         dataIndex: 'updatedAt',
         key: 'updatedAt',
         width: 80,
         render: (value: any) => (
            <span className="txtMax2Line">{moment(value).format(DATE_TIME_FORMAT_CLIENT_short2)}</span>
         )
      },
      {
         title: 'Seller OrderID',
         dataIndex: 'sellerOrderId',
         key: 'sellerOrderId',
         width: 140,
         render: (value: any) => <span className="txtMax2Line">{value}</span>
      },
      {
         title: 'QTY',
         dataIndex: 'qty',
         width: 50,
         key: 'qty',
         render: (value: any, record: any) => {
            const qty =
               value ||
               getProductsInOrder(record).reduce((sum: number, item: any) => sum + parseFloat(item?.qty || 0), 0)
            return <span className="xtMax2Line">{qty}</span>
         }
      },
      {
         title: 'Ship to',
         dataIndex: 'shipLabel',
         width: 150,
         key: 'shipLabel',
         render: (value: any, record: any) => {
            if (record?.shipLabel) {
               return (
                  <a href={`${record.shipLabel}`} target="_blank" rel="noreferrer">
                     <span className="gx-link txtMax2Line" style={{ fontSize: 11 }}>
                        {record.shipLabel}
                     </span>
                  </a>
               )
            }

            return (
               <div style={{ fontSize: 11 }}>
                  <div>
                     Customer: <span className="gx-link txtMax2LineHover">{record?.customer}</span>
                  </div>
                  <div>
                     Phone: <span className="gx-link txtMax2LineHover">{record?.phone}</span>
                  </div>
                  <div>
                     Address:{' '}
                     <span className="gx-link txtMax2LineHover">{`${record?.address1 || ''}${
                        record?.address2 ? `- ${record.address2}` : ''
                     } - ${record?.city || ''} - ${record?.state || ''} - ${record?.zipCode || ''}`}</span>
                  </div>
               </div>
            )
         }
      },
      {
         title: 'Note',
         dataIndex: 'note',
         width: 80,
         key: 'note',
         render: (value: any) => (
            <span className="xtMax2Line">{value && value.length > 20 ? value.slice(0, 20) + '...' : value}</span>
         )
      },
      {
         title: 'Total',
         dataIndex: 'subTotalPrice',
         width: 80,
         key: 'subTotalPrice',
         render: (value: any, record: any) => (
            <span className="txtMax2Line">
               ${formatCurrency(value || record?.subTotalPriceSeller || record?.totalPrice || 0)}
            </span>
         )
      },
      {
         title: 'Action',
         key: 'action',
         width: 150,
         align: 'center' as 'center',
         fixed: 'right' as 'right',
         render: (_: any, record: any) => (
            <span className="txtMax3LineHover">
               <span onClick={() => handleShowDetail(record)} className="gx-link txtMax3LineHover">
                  Chi tiết
               </span>
               <Divider type="vertical" />
               <span onClick={() => handleConfirmOrder(record)} className="gx-link txtMax3LineHover">
                  {getCreateOrderLabel(record)}
               </span>
               <Divider type="vertical" />
               <span onClick={() => handleRemoveOrder(record.draftId)} className="gx-link txtMax3LineHover">
                  Xóa
               </span>
            </span>
         )
      }
   ]

   const rowSelection = {
      onChange: (_selectedRowKeys: any, rowsSelected: any[]) => {
         setSelectedRows(rowsSelected)
      }
   }

   const renderPagination = () => {
      if (isEmpty(rows)) return null

      return (
         <div className="containerPagination" ref={paginationRef}>
            <Pagination
               onChange={(currentPage) => getListData(currentPage - 1, { keyword: trim(textSearch) })}
               pageSize={PAGE_SIZE}
               current={OrdersStore.offsetList / PAGE_SIZE + 1}
               showSizeChanger={false}
               total={count}
            />
         </div>
      )
   }

   return (
      <>
         <style>{`
            .draft-order-row td,
            .draft-order-row.ant-table-row-selected td {
               background: #fffdf7 !important;
            }

            .draft-order-row:hover td,
            .draft-order-row.ant-table-row-selected:hover td {
               background: #fff9ed !important;
            }

            .draft-order-row.ant-table-row-selected td {
               background: #fff8e8 !important;
            }

            .draft-order-code {
               display: flex;
               flex-direction: column;
               align-items: flex-start;
               gap: 4px;
            }

            .draft-order-badge {
               color: #d46b08;
               background: #fff7e6;
               border: 1px solid #ffd591;
               border-radius: 4px;
               padding: 1px 4px;
               white-space: nowrap;
               font-size: 10px;
               line-height: 16px;
            }

            .draft-product-list {
               display: flex;
               flex-direction: column;
            }

            .draft-product-row {
               min-height: 86px;
               padding: 6px 0;
               display: flex;
               flex-direction: column;
               justify-content: center;
               align-items: flex-start;
            }

            .draft-product-name {
               margin-top: 4px;
               font-size: 10px;
            }

            .draft-link-text {
               max-width: 210px;
               display: -webkit-box;
               -webkit-line-clamp: 3;
               -webkit-box-orient: vertical;
               overflow: hidden;
               word-break: break-all;
               line-height: 18px;
            }

            .draft-empty-link {
               color: #999;
            }
         `}</style>
         <Loading isLoading={isLoading} />
         <Row>
            <Col span={24}>
               <Row className="row-container-head">
                  <Col xs={24} md={8}>
                     <Input.Search
                        onSearch={(value) => setTextSearch(value)}
                        placeholder="Tên SP/TrackingId/Tên người nhận/OrderId/SellerOrderId"
                        allowClear
                     />
                  </Col>
                  <Col xs={24} md={16}>
                     <div className="text-right">
                        <Space>
                           <DatePicker.RangePicker
                              allowClear={false}
                              defaultValue={[OrdersStore.startTime, OrdersStore.endTime]}
                              format={DATE_FORMAT_CLIENT}
                              locale={locale}
                              onChange={handleRangePickerChange}
                           />
                           <ExportToCSV
                              getData={refreshCurrentPage}
                              hideExport
                              importMode="draft"
                              importButtonText="Nhập Excel"
                           />
                           <Button type="primary" className="btn-add" onClick={() => setVisibleCreate(true)}>
                              Tạo đơn nháp
                           </Button>
                        </Space>
                     </div>
                  </Col>
               </Row>

               <Space className="total-items" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <p>Tổng số: {count}</p>
                  {selectedRows.length >= 1 && (
                     <Button type="primary" className="btn-add" onClick={handleConfirmSelected}>
                        {getCreateMultiOrderLabel()}
                     </Button>
                  )}
               </Space>

               <div ref={tableWrapperRef}>
                  <Table
                     scroll={{ x: 1400, y: scrollY }}
                     columns={columns}
                     dataSource={rows}
                     rowKey="draftId"
                     rowClassName={() => 'draft-order-row'}
                     rowSelection={rowSelection}
                     pagination={false}
                  />
               </div>

               {renderPagination()}
            </Col>
         </Row>

         {visibleCreate && (
            <CreateOrder
               visible={visibleCreate}
               forceDraftMode
               onOk={handleCreateDone}
               onCancel={() => setVisibleCreate(false)}
               onShowProducts={() => setVisibleProductModal(true)}
            />
         )}

         {visibleProductModal && (
            <SelectProductModal
               visible={visibleProductModal}
               onOk={() => setVisibleProductModal(false)}
               onCancel={() => setVisibleProductModal(false)}
            />
         )}

         {visibleDetail && selectedItem && (
            <CreateOrder
               onlyView
               isDraftDetail
               visible={visibleDetail}
               item={selectedItem}
               onConfirmDraftOrder={() => handleConfirmOrder(selectedItem, hideDetail)}
               onShowProducts={() => setVisibleProductModal(true)}
               onOk={() => {
                  hideDetail()
                  refreshCurrentPage()
               }}
               onCancel={hideDetail}
            />
         )}
      </>
   )
})

export default DraftOrderManagement
